import React from "react";
import { Navigate } from "react-router-dom";
import { canSeeSupportAgentScreen, DocStatus } from "./store";

// Route guard so a hidden screen can't be opened by URL either: crm_admin
// lands on Setup, anyone without Setup lands on Knowledge.
export const SupportAgentRoute: React.FC<{ screen: string; children: React.ReactElement }> = ({ screen, children }) => {
  if (canSeeSupportAgentScreen(screen)) return children;
  const fallback = canSeeSupportAgentScreen("Setup") ? "/support-bot/setup" : "/support-bot/knowledge";
  return <Navigate to={fallback} replace />;
};

const STATUS_STYLE: Record<DocStatus, { label: string; cls: string }> = {
  live: { label: "Live", cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400" },
  pending: { label: "Pending approval", cls: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400" },
  changes_requested: { label: "Changes requested", cls: "bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400" },
  failed: { label: "Failed validation", cls: "bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400" },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400" },
};

// My Submissions shows an approved document as "Approved" rather than "Live".
export const StatusBadge: React.FC<{ status: DocStatus; approvedLabel?: boolean }> = ({ status, approvedLabel }) => {
  const s = STATUS_STYLE[status];
  return (
    <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}>
      {approvedLabel && status === "live" ? "Approved" : s.label}
    </span>
  );
};

export const PageHeader: React.FC<{ title: string; subtitle: string; action?: React.ReactNode }> = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
    <div>
      <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
      <p className="text-sm text-gray-500 font-semibold dark:text-gray-400 mt-0.5">{subtitle}</p>
    </div>
    {action}
  </div>
);

export const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
      active
        ? "bg-indigo-600 text-white border-indigo-600"
        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
    }`}
  >
    {children}
  </button>
);

export const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-12">{text}</p>
);

export const primaryBtn =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
export const secondaryBtn =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 transition-colors";
export const inputCls =
  "w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20";
